import React, { useEffect, useState } from 'react';
import Logger from '../../../shared/Logger';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { useLazyQuery } from '@apollo/client';
import { Formik, FormikValues, FormikActions } from 'formik';
import * as yup from 'yup';
import {
  Button,
  FormMessage,
  Input,
  Label,
  Panel,
  theme,
  Text,
  Box,
  Flex,
  Flow,
} from '@xometry/xometry_loft';
import { forgotPasswordStyles } from './ULPForgotPassword.styled';
import { requiredMessage, email } from '../../../utils/validations';
import passwordResetServices from '../../../services/PasswordReset';
import { WWW_ORIGIN } from '../../../config';
import { IsEmailInAuth0Query } from '../../../schema/generated/schema';

const schema = yup.object().shape({
  email_address: email.required(requiredMessage),
});

const ensureString = (input: string | string[]): string => {
  if (Array.isArray(input)) {
    return input.join(','); // making sure we are dealing with strings since sendPasswordResetEmails expects a string
  }
  return input;
};

export const ULPForgotPassword = () => {
  window.analytics.page('Forgot Password');

  const location = useLocation();
  const navigate = useNavigate();

  const submitOnLoad = queryString.parse(location.search).submitOnLoad || false;

  const [checkEmail] = useLazyQuery(IsEmailInAuth0Query);

  const [errorOnEmail, setErrorOnEmail] = useState(false);

  const email_address = queryString.parse(location.search).email_address || '';
  const emailAddressString = ensureString(email_address);

  const handleAuth0PasswordReset = async (
    emailAddress: string,
    encodedEmail: string
  ) => {
    try {
      await passwordResetServices.sendResetPasswordEmail(emailAddress);
      navigate(`/account/ulp-reset-password?email_address=${encodedEmail}`);
    } catch (error) {
      setErrorOnEmail(true);
      Logger.error('Error sending Auth0 reset password email', error, {
        component: 'ULPForgotPassword',
        action: 'sendAuth0ResetEmail',
      });
    }
  };

  const sendPasswordResetEmails = async (emailAddress: string) => {
    const encodedEmail = encodeURIComponent(emailAddress);
    setErrorOnEmail(false);

    try {
      const isUserAuth0 = await checkEmail({
        variables: {
          email: emailAddress,
        },
      });

      if (isUserAuth0.data.isEmailInAuth0) {
        // if user exists in auth0 then go to Check Your Email page
        // which will send a reset password link email.
        await handleAuth0PasswordReset(emailAddress, encodedEmail);
      } else {
        // The user does not exist in Auth0
        setErrorOnEmail(true);
        Logger.error('Error sending reset password email', undefined, {
          component: 'ULPForgotPassword',
          action: 'sendResetEmail',
        });
      }
    } catch (error) {
      setErrorOnEmail(true);
      Logger.error('Error checking email', error, {
        component: 'ULPForgotPassword',
        action: 'checkEmail',
      });
    }
  };

  useEffect(() => {
    if (submitOnLoad && emailAddressString.length > 0) {
      sendPasswordResetEmails(emailAddressString);
    }
  }, []);

  const handleSubmit = async (
    values: FormikValues,
    actions: FormikActions<FormikValues>
  ) => {
    actions.setSubmitting(true);
    try {
      sendPasswordResetEmails(values.email_address);
    } catch (e: unknown) {
      throw new Error(
        `Could not query for user: ${values.email_address}`,
        e as ErrorOptions
      );
    }
    actions.setSubmitting(false);
  };

  const handleBackToLogin = () => {
    if (document.referrer.includes('login')) {
      window.history.back();
    } else {
      window.location.href = `${WWW_ORIGIN}/login?email=${encodeURIComponent(
        email_address as string
      )}`;
    }
  };

  return (
    <>
      <Formik
        initialValues={{ email_address }}
        onSubmit={handleSubmit}
        validationSchema={schema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => {
          const invalid =
            Boolean(
              errors.email_address &&
                touched.email_address &&
                errors.email_address
            ) || errorOnEmail;
          const { email_address } = values;
          return (
            <Panel kind="secondary" css={forgotPasswordStyles.panel}>
              <img
                css={forgotPasswordStyles.logoXomThom}
                id="prompt-logo-center"
                src="https://assets.xometry.com/images/Xometry_Blue_Registered%402x.png"
                alt="Xometry"
              />
              <form onSubmit={handleSubmit}>
                <Flex flexDirection="column">
                  <Text
                    kind="panel-title"
                    fontWeight={600}
                    margin="20px 0 12px"
                    width="100%"
                    textAlign="center"
                  >
                    Forgot your password?
                  </Text>
                  <Text
                    kind="body-sm"
                    width="100%"
                    textAlign="center"
                    marginBottom="12px"
                  >
                    Enter your email, and we will send you instructions to reset
                    your password.
                  </Text>
                </Flex>
                <Box width="100%" marginBottom={theme.space[3]}>
                  <Label htmlFor="email_address">Email Address</Label>
                  <Input
                    className="pii-encrypt"
                    id="email_address"
                    name="email_address"
                    width="100%"
                    lineHeight="34px"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={email_address}
                    invalid={invalid}
                  />
                  {errors.email_address && touched.email_address && (
                    <FormMessage invalid={invalid}>
                      {errors.email_address}
                    </FormMessage>
                  )}
                  {errorOnEmail && (
                    <FormMessage invalid={true}>
                      Email not found. Please try another email.
                    </FormMessage>
                  )}
                </Box>
                <Button
                  type="submit"
                  kind={theme.kind.primary}
                  width="100%"
                  height={50}
                  padding={`${theme.space[3]} 0`}
                  fontWeight={theme.fontWeights.semibold}
                  fontSize={theme.fontSizes[3]} // 20px
                  margin={`${theme.space[3]} 0`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Flow scale="panel" kind="disabled" />
                  ) : (
                    'Continue'
                  )}
                </Button>
              </form>
              <Button
                kind="text-blue"
                width="100%"
                padding={`${theme.space[3]} 0`}
                fontWeight={theme.fontWeights.semibold}
                marginBottom={theme.space[2]}
                disabled={isSubmitting}
                onClick={handleBackToLogin}
              >
                Back to Login
              </Button>
            </Panel>
          );
        }}
      </Formik>
    </>
  );
};
